import { initTdx } from '../../init/sdkTdx';
import { tdx_request, tdx_response } from "../../tdx_proto/bundle";
import { getConnectionPool } from '../../core/connectionPool';
import { hashString } from '../../utils/hash';

export type SendQueryProgress = {
    stage: 'connecting' | 'fetching' | 'completed';
    pagesFetched: number;
    rowsFetched: number;
    lastBatchSize: number;
    bytesFetched: number;
    limitReached: boolean;
};

export type SendQueryBatch = {
    pageNumber: number;
    rows: string[];
    grammarObjects: any[];
};

type SendQueryOptions = {
    pageSize?: number;
    maxRows?: number;
    onProgress?: (progress: SendQueryProgress) => void;
    onBatch?: (batch: SendQueryBatch) => void;
};

export async function sendQuery(query: string, options: SendQueryOptions = {}) {
    // await sdkInit();
    const pool = await getConnectionPool();
    const pin = await hashString(pool.getPin());
    const data = pool.getToken();
    const client = await initTdx();
    const grammarObjects: any[] = [];
    const pageSize = Math.min(1000, Math.max(50, options.pageSize ?? 1000));
    const maxRows = Math.max(0, options.maxRows ?? 20000);
    const onProgress = options.onProgress;
    const onBatch = options.onBatch;

    onProgress?.({
        stage: 'connecting',
        pagesFetched: 0,
        rowsFetched: 0,
        lastBatchSize: 0,
        bytesFetched: 0,
        limitReached: false,
    });

    let times = 10;
    while (client.is_open() == false && times >= 0) {
        console.log('🔄 Waiting for WebSocket to open...');
        times -= 1;
        await new Promise(resolve => setTimeout(resolve, 1000));

    }
    if (times < 0) {
        throw new Error("Connection Failed. Please try again.");
    }

    // Example request for ListLogsFromQuery
    let page_token: Uint8Array = new Uint8Array();
    const all_json_rows: string[] = [];
    let pagesFetched = 0;
    let limitReached = false;
    while (true) {
        const req = tdx_request.Request.create({
        listLogs: tdx_request.ListLogsFromQuery.create({
            query: query,
            pin: pin,
            data: data, // or provide actual bytes if needed
            pageToken: page_token, // or provide actual bytes if needed
            pageSize: pageSize
        })
    });
     try {
        const res = await client.sendAndWait(req);
        if (!res.listLogs || res.listLogs.status!==tdx_response.Status.SUCCESS){
            throw new Error("No data returned. Please check your query or try again later.");
        }
        if (page_token.length === 0) {
            // For the first request, get the grammar JSONs from the response
            let grammar_jsons = res.listLogs?.graphGrammarJsons || [];
            for (const grammar of grammar_jsons) {
            try {
                const obj = JSON.parse(grammar);
                grammarObjects.push(obj);
            } catch (e) {
                console.error('Failed to parse grammar JSON:', grammar, e);
            }
            }
        }
        page_token = res.listLogs?.pageToken ?? new Uint8Array();
        const rawRows = res.listLogs?.rows?.map(row => row instanceof Uint8Array ? row : new Uint8Array()) || [];
        const json_rows: string[] = [];

        for (const rowBytes of rawRows) {
            if (all_json_rows.length + json_rows.length >= maxRows) {
                console.warn(`Row limit of ${maxRows} reached. Stopping further fetches.`);
                limitReached = true;
                break;
            }

            json_rows.push(new TextDecoder('utf-8').decode(rowBytes));
        }

        all_json_rows.push(...json_rows);
        pagesFetched += 1;
        onBatch?.({
            pageNumber: pagesFetched,
            rows: json_rows,
            grammarObjects: [...grammarObjects],
        });
        onProgress?.({
            stage: 'fetching',
            pagesFetched,
            rowsFetched: all_json_rows.length,
            lastBatchSize: json_rows.length,
            bytesFetched: 0,
            limitReached,
        });
        if (page_token.length === 0 || limitReached) {
            // console.log('✅ No more pages to fetch, breaking the loop.');
            break;
        }
        console.log(`📄 Fetched ${json_rows.length} rows, next page token length: ${page_token.length}`);
    } catch (err) {
        throw Error('Error in request:'+ err);
    }
    }
    client.close();
    onProgress?.({
        stage: 'completed',
        pagesFetched,
        rowsFetched: all_json_rows.length,
        lastBatchSize: 0,
        bytesFetched: 0,
        limitReached,
    });
    // console.log('📦 All JSON Rows:', all_json_rows);
    return [all_json_rows, grammarObjects];

}

